import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import config from "../../config/config";
import styles from "./SponsorsDetail.module.css";
import { SponsorService } from "../../services/sponsor_service";
import { SponsorAdvertisementService } from "../../services/sponsor_advertisement_service";
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaBuilding,
  FaChartBar,
  FaCalendarAlt,
  FaSyncAlt,
  FaBullhorn,
  FaMapMarkedAlt,
} from "react-icons/fa";
import { useSponsorDetailState } from "../../states/useSponsorDetail";
const SponsorDetail = () => {
  const sponsorService = SponsorService();
  const { id } = useParams();
  const navigate = useNavigate();
  const sponsorAdvertisementService = SponsorAdvertisementService();
  const {sponsor, setSponsor, loading, setLoading, error, setError, sponsorAds, setSponsorAds} = useSponsorDetailState();
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchSponsorDetail();
  }, [id]);

  const fetchSponsorDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const sponsorData = await sponsorService.fetchSponsorById(id);
      setSponsor(sponsorData);

      // Fetch all sponsor ads and filter by current sponsor
      await sponsorAdvertisementService.fetchAllSponsorAdvertisement((ads) => {
        const filteredAds = ads.filter(
          (ad) => ad.sponsor && ad.sponsor.toString() === id
        );
        setSponsorAds(filteredAds);
      });
    } catch (error) {
      console.error("Error fetching sponsor details", error);
      setError("Failed to load sponsor details");
    } finally {
      setLoading(false);
    }
  };

  const getFullImageUrl = (iconPath) => {
    if (!iconPath) return "/default-sponsor.png";
    if (iconPath.startsWith("http")) return iconPath;
    return `${config.baseURL}${iconPath}`;
  };

  const handleAdClick = (ad) => {
    if (ad.target_url) {
      window.open(ad.target_url, "_blank", "noopener,noreferrer");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleContact = (type, value) => {
    switch (type) {
      case "email":
        window.location.href = `mailto:${value}`;
        break;
      case "phone":
        window.location.href = `tel:${value}`;
        break;
      default:
        break;
    }
  };

  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const truncateText = (text, length = 120) => {
    if (!text) return "";
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading Sponsor Details...</p>
      </div>
    );
  }

  if (error || !sponsor) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <h2>Sponsor Not Found</h2>
          <p>
            The sponsor you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate("/sponsors")}
            className={styles.backButton}
          >
            Back to Sponsors
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.sponsorDetailPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          <button onClick={() => navigate(-1)} className={styles.backButton}>
              <FaArrowLeft />  Back
            </button>
          <img
            src={getFullImageUrl(sponsor.logo)}
            alt={sponsor.name}
            className={styles.heroImage}
          />
        </div>
        <div className={styles.heroContent}>
          <div className={styles.container}>
            
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className={styles.detailsSection}>
        <div className={styles.container}>
          <div className={styles.detailsGrid}>
            {/* Main Content */}
            <div className={styles.mainContent}>
              {/* Description Section */}
              <div className={styles.descriptionSection}>
                <h2>About {sponsor.name}</h2>
                <div className={styles.eventDescription}>
                  <h3 >{sponsor.name}</h3>
                  {sponsor.description ? (
                    <div
                      className={styles.sponsorDescription}
                      dangerouslySetInnerHTML={{
                        __html: (
                          sponsor.description
                        ),
                      }}
                    />
                  ) : (
                    <p className={styles.sponsorDescription}>
                      No description available for this sponsor.
                    </p>
                  )}

                  {/* Contact Information */}
                  {(sponsor.email || sponsor.phone) && (
                    <>
                      <h3 style={{marginBottom: "0"}}>Contact Details :</h3>
                      <div className={styles.contactGrid}>
                        {sponsor.email && (
                          <div className={styles.contactItem}>
                            <span className={styles.contactLabel}>Email:</span>
                            <span
                              className={styles.contactLink}
                              onClick={() =>
                                handleContact("email", sponsor.email)
                              }
                            >
                              {sponsor.email}
                            </span>
                          </div>
                        )}
                        {sponsor.phone && (
                          <div className={styles.contactItem}>
                            <span className={styles.contactLabel}>Phone:</span>
                            <span
                              
                            >
                              {sponsor.phone}
                            </span>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Address Information */}
                  {(sponsor.address_line1 || sponsor.city) && (
                    <>
                      <h3 style={{marginBottom: "0"}}>Address : </h3>
                      <div className={styles.addressSection}>
                        {sponsor.address_line1 && (
                          <p className={styles.addressLine}>
                            {sponsor.address_line1}
                          </p>
                        )}
                        {sponsor.address_line2 && (
                          <p className={styles.addressLine}>
                            {sponsor.address_line2}
                          </p>
                        )}
                        <div className={styles.addressDetails}>
                          {sponsor.city && <span>{sponsor.city}</span>}
                          {sponsor.state && <span>, {sponsor.state}</span>}
                          {sponsor.postal_code && (
                            <span> - {sponsor.postal_code}</span>
                          )}
                          {sponsor.country && <span>, {sponsor.country}</span>}
                        </div>
                        {sponsor.address_line1 && sponsor.city && (
                          <a
                            href={`https://maps.google.com/?q=${encodeURIComponent(
                              `${sponsor.address_line1}, ${sponsor.city}, ${
                                sponsor.state || ""
                              } ${sponsor.postal_code || ""}, ${
                                sponsor.country || ""
                              }`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.mapButton}
                          >
                            <FaMapMarkedAlt /> View on Map
                          </a>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Representative Information */}
              {(sponsor.representative_name ||
                sponsor.representative_email ||
                sponsor.representative_phone) && (
                <div className={styles.agendaSection}>
                  <h2>Representative Information</h2>
                  <div className={styles.representativeGrid}>
                    {sponsor.representative_name && (
                      <div className={styles.repItem}>
                        <div className={styles.repIcon}><FaUser /></div>
                        <div className={styles.repDetails}>
                          <div className={styles.repLabel}>Representative</div>
                          <div className={styles.repValue}>
                            {sponsor.representative_name}
                          </div>
                        </div>
                      </div>
                    )}
                    {sponsor.representative_email && (
                      <div className={styles.repItem}>
                        <div className={styles.repIcon}><FaEnvelope /></div>
                        <div className={styles.repDetails}>
                          <div className={styles.repLabel}>Email</div>
                          <div
                            className={styles.repValue}
                            onClick={() =>
                              handleContact(
                                "email",
                                sponsor.representative_email
                              )
                            }
                          >
                            {sponsor.representative_email}
                          </div>
                        </div>
                      </div>
                    )}
                    {sponsor.representative_phone && (
                      <div className={styles.repItem}>
                        <div className={styles.repIcon}><FaPhone /></div>
                        <div className={styles.repDetails}>
                          <div className={styles.repLabel}>Phone</div>
                          <div
                            className={styles.repValue}
                            onClick={() =>
                              handleContact(
                                "phone",
                                sponsor.representative_phone
                              )
                            }
                          >
                            {sponsor.representative_phone}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className={styles.sidebar}>
              <div className={styles.eventInfoCard}>
                <h3>Sponsor Information</h3>
                {sponsor.name && (
                  
                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}><FaBuilding /></div>
                  <div className={styles.infoContent}>
                    <div className={styles.infoLabel}>Company</div>
                    <div className={styles.infoValue}>{sponsor.name}</div>
                  </div>
                </div>
                )}
                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}><FaChartBar /></div>
                  <div className={styles.infoContent}>
                    <div className={styles.infoLabel}>Status</div>
                    <div className={styles.infoValue}>
                      {sponsor.is_active ? (
                        <span className={styles.activeStatus}>Active</span>
                      ) : (
                        <span className={styles.inactiveStatus}>Inactive</span>
                      )}
                    </div>
                  </div>
                </div>

                {sponsor.city && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoIcon}><FaMapMarkerAlt /></div>
                    <div className={styles.infoContent}>
                      <div className={styles.infoLabel}>Location</div>
                      <div className={styles.infoValue}>
                        {sponsor.city}
                        {sponsor.country && `, ${sponsor.country}`}
                      </div>
                    </div>
                  </div>
                )}

                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}><FaCalendarAlt /></div>
                  <div className={styles.infoContent}>
                    <div className={styles.infoLabel}>Member Since</div>
                    <div className={styles.infoValue}>
                      {formatDate(sponsor.created_at)}
                    </div>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}><FaSyncAlt /></div>
                  <div className={styles.infoContent}>
                    <div className={styles.infoLabel}>Last Updated</div>
                    <div className={styles.infoValue}>
                      {formatDate(sponsor.updated_at)}
                    </div>
                  </div>
                </div>

                {sponsor.representative_name && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoIcon}><FaUser /></div>
                    <div className={styles.infoContent}>
                      <div className={styles.infoLabel}>Representative</div>
                      <div className={styles.infoValue}>
                        {sponsor.representative_name}
                      </div>
                    </div>
                  </div>
                )}

                {/* Advertisement Count */}
                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}><FaBullhorn /></div>
                  <div className={styles.infoContent}>
                    <div className={styles.infoLabel}>Active Ads</div>
                    <div className={styles.infoValue}>
                      {sponsorAds.filter((ad) => ad.is_active).length}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Sponsor Advertisements Section */}
          {sponsorAds.length > 0 ? (
            <div className={styles.adsSection}>
              <h2>Our Advertisements</h2>
              <div className={styles.adsGrid}>
                {sponsorAds.map((ad, index) => (
                  <div key={ad.id || index} className={styles.adCard}>
                    <div className={styles.adContent}>
                      <div className={styles.adMediaContainer}>
                        {ad.ad_type === "video" ? (
                          <video
                            className={styles.adMedia}
                            autoPlay
                            muted
                            loop
                            playsInline
                          >
                            <source
                              src={getFullImageUrl(ad.media_file)}
                              type="video/mp4"
                            />
                          </video>
                        ) : (
                          <img
                            src={getFullImageUrl(ad.media_file)}
                            alt={ad.title || `${sponsor.name} Advertisement`}
                            className={styles.adMedia}
                            onError={(e) => {
                              e.target.style.display = "none";
                              const fallback =
                                e.target.parentNode.querySelector(
                                  `.${styles.adFallback}`
                                );
                              if (fallback) fallback.style.display = "flex";
                            }}
                          />
                        )}

                        <div className={styles.adFallback}>
                          <div className={styles.fallbackLogo}><FaBuilding /></div>
                          <div className={styles.fallbackText}>
                            <div className={styles.fallbackCompany}>
                              {sponsor.name}
                            </div>
                          </div>
                        </div>

                        {ad.target_url && (
                          <div className={styles.adOverlay}>
                            <button
                              className={styles.adButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAdClick(ad);
                              }}
                            >
                              Learn More
                            </button>
                          </div>
                        )}
                      </div>

                      <div className={styles.adInfo}>
                        <Link
                          to={`/advertisement-detail/${ad.id}`}
                          style={{ textDecoration: "none" }}
                        >
                          {ad.title && (
                            <h4 className={styles.adTitle}>{ad.title} →</h4>
                          )}
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.noAdsSection}>
              <div className={styles.noAdsContent}>
                <div className={styles.noAdsIcon}><FaBullhorn /></div>
                <h3>No Advertisements Available</h3>
                <p>
                  This sponsor doesn't have any active advertisements at the
                  moment.
                </p>
                <p className={styles.noAdsSubtext}>
                  Check back later for updates.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SponsorDetail;
